/**
 * Word-level caption timing from the Live API's output transcript.
 *
 * The API gives transcript text in deltas, with no timings of its own — but
 * every delta is stamped here with the PCM byte position at the moment it
 * arrived (see pcm.ts), which is an exact offset into the generated audio. The
 * words inside one delta are then spread across the gap to the next delta,
 * weighted by length, which is what lets a caption highlight the word being
 * spoken instead of just the sentence.
 *
 * That spread is an approximation: it assumes an even speaking rate within a
 * delta. Deltas are short (a handful of words), so the error stays well under
 * the length of a word.
 */
import type { CaptionLine, CaptionWord, TranscriptDelta } from "./types";

/** Nudge every caption if the transcript consistently leads or lags the audio. */
const CAPTION_OFFSET_MS = 0;

/**
 * Captions are always rendered on ONE line — two lines make the pill wide and
 * tall enough to swamp the frame. These caps keep a group short enough that the
 * renderer rarely has to shrink the type to fit.
 */
const MAX_LINE_CHARS = 24;
const MAX_LINE_WORDS = 5;
const MIN_WORD_MS = 90;

export function buildWordTimeline({
	deltas,
	durationMs,
}: {
	deltas: TranscriptDelta[];
	durationMs: number;
}): CaptionWord[] {
	const words: CaptionWord[] = [];

	for (let i = 0; i < deltas.length; i++) {
		const tokens = deltas[i].text.split(/\s+/).filter(Boolean);
		if (tokens.length === 0) continue;

		const start = clamp({
			value: deltas[i].atMs + CAPTION_OFFSET_MS,
			min: 0,
			max: durationMs,
		});
		const end = clamp({
			value: (deltas[i + 1]?.atMs ?? durationMs) + CAPTION_OFFSET_MS,
			min: start,
			max: durationMs,
		});

		// Weight by length so "internationalisation" gets more room than "a".
		const weights = tokens.map((token) => token.length + 1);
		const total = weights.reduce((sum, weight) => sum + weight, 0);
		const span = Math.max(end - start, tokens.length * MIN_WORD_MS);

		let cursor = start;
		tokens.forEach((token, index) => {
			const width = (weights[index] / total) * span;
			words.push({
				text: token,
				startMs: cursor,
				endMs: cursor + width,
			});
			cursor += width;
		});
	}

	// Deltas can overlap slightly; keep the sequence strictly forward-moving so
	// the "which word is active now" lookup is unambiguous.
	for (let i = 1; i < words.length; i++) {
		if (words[i].startMs < words[i - 1].endMs) {
			words[i].startMs = words[i - 1].endMs;
		}
		if (words[i].endMs < words[i].startMs + MIN_WORD_MS) {
			words[i].endMs = words[i].startMs + MIN_WORD_MS;
		}
	}

	return words;
}

const SENTENCE_END = /[.!?。！？…]["')\]]?$/;

export function buildCaptionLines({
	words,
}: {
	words: CaptionWord[];
}): CaptionLine[] {
	const lines: CaptionLine[] = [];
	let current: CaptionWord[] = [];
	let chars = 0;

	const flush = () => {
		if (current.length === 0) return;
		lines.push({
			words: current,
			startMs: current[0].startMs,
			endMs: current[current.length - 1].endMs,
		});
		current = [];
		chars = 0;
	};

	for (const word of words) {
		const wouldOverflow =
			current.length > 0 &&
			(chars + word.text.length + 1 > MAX_LINE_CHARS ||
				current.length >= MAX_LINE_WORDS);
		if (wouldOverflow) flush();

		current.push(word);
		chars += word.text.length + 1;

		// Break on sentence end so a line never straddles two thoughts.
		if (SENTENCE_END.test(word.text)) flush();
	}
	flush();

	// A line stays up until the next one starts, so there is never a blank gap
	// mid-sentence while the narrator draws breath.
	for (let i = 0; i < lines.length - 1; i++) {
		lines[i].endMs = lines[i + 1].startMs;
	}

	return lines;
}

export function findActiveLine({
	lines,
	timeMs,
}: {
	lines: CaptionLine[];
	timeMs: number;
}): CaptionLine | null {
	for (const line of lines) {
		if (timeMs >= line.startMs && timeMs < line.endMs) return line;
	}
	return null;
}

export function findActiveWordIndex({
	line,
	timeMs,
}: {
	line: CaptionLine;
	timeMs: number;
}): number {
	for (let i = line.words.length - 1; i >= 0; i--) {
		if (timeMs >= line.words[i].startMs) return i;
	}
	return -1;
}

function clamp({
	value,
	min,
	max,
}: {
	value: number;
	min: number;
	max: number;
}): number {
	return Math.min(max, Math.max(min, value));
}
